// ============================================================================
// CANCUN100 — FinanzasTab.jsx  (FASE FINANZAS v1 — tab admin, 100% LECTURA)
// ----------------------------------------------------------------------------
// Solo SELECT sobre 7 vistas ya creadas en Supabase. NO escribe nada, NO toca
// matemática de comisiones (reutiliza fórmulas Fase 7) ni vistas/tablas SQL.
// Recibe C (paleta activa; admin va forzado dark) y agentes (ya cargados en el
// admin) como props para evitar imports circulares con App.js.
//
// NOTA para Marco: los nombres de columna se toman del brief. Donde el brief no
// fija un nombre exacto se usa un "pick" tolerante (primer nombre presente).
// Si alguna vista usa otro nombre, es un ajuste de una línea en los arrays pick.
// ============================================================================
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import GastosTab from './GastosTab';
import { etiquetaReglaAdmin } from './reglasAdmin';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';
import { COLOR_EQUIPO } from './temaEquipo'; // APP 2/20 Fase A: turquesa TT desde el tema, no suelta
import { Ic } from './iconos'; // APP 2/34: íconos Lucide en vez de emoji

// ── Helpers de formato (moneda es-MX, mismo criterio que el admin) ──
const F0 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const F2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => '$' + F0.format(Number(n || 0));
const money2 = (n) => '$' + F2.format(Number(n || 0));

// pick tolerante: primer nombre de columna presente (numérico / texto / fecha)
const num = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== '') return Number(o[k]) || 0; } return 0; };
const str = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== '') return String(o[k]); } return ''; };
const dcol = (o, keys) => { for (const k of keys) { if (o && o[k]) return o[k]; } return null; };

const FECHA_KEYS = ['created_at', 'fecha', 'fecha_reserva', 'fecha_creacion'];

// ── CSV client-side (UTF-8 con BOM para Excel, separador coma) ──
const csvEscape = (v) => {
  const s = String(v == null ? '' : v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};
const buildCSV = (headers, rows) => {
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach((r) => lines.push(r.map(csvEscape).join(',')));
  return '﻿' + lines.join('\r\n'); // BOM + CRLF
};
const downloadCSV = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── Semana lunes→domingo ──
const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // lunes=0
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0);
  return x;
};
const ymd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export default function FinanzasTab({ C, agentes = [], adminName = 'Admin', foliosAdmin = {}, adminsNombres = {}, reglasAdminFolio = {} }) {
  const [subtab, setSubtab] = useState('resumen'); // 'resumen' | 'gastos'
  const [canal, setCanal] = useState('todos');     // Bloque 24: 'todos' | 'sala' | 'tt' | 'admin'
  const [loading, setLoading] = useState(true);
  const [errs, setErrs] = useState([]);            // vistas que fallaron
  const [mov, setMov] = useState([]);              // finanzas_movimientos
  const [folios, setFolios] = useState([]);        // finanzas_folios
  const [resp, setResp] = useState([]);            // finanzas_responsabilidad
  const [desc, setDesc] = useState([]);            // finanzas_descuadres
  const [prov, setProv] = useState([]);            // finanzas_proveedores
  const [ctrips, setCtrips] = useState([]);        // finanzas_cancuntrips
  const [vagente, setVagente] = useState([]);      // finanzas_ventas_agente

  const [periodo, setPeriodo] = useState('mes');   // mes|mes-pasado|q1..q4|anio|todos
  const [alertaFiltro, setAlertaFiltro] = useState('todas');
  const [modal, setModal] = useState(null);        // {label, color, rows}
  // Semana por defecto: la semana pasada completa (lunes→domingo)
  const [weekStart, setWeekStart] = useState(() => {
    const m = mondayOf(new Date()); m.setDate(m.getDate() - 7); return m;
  });

  // ── Carga lazy de las 7 vistas (solo al montar el tab) ──
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const q = (t) => supabase.from(t).select('*');
      const [r1, r2, r3, r4, r5, r6, r7] = await Promise.all([
        q('finanzas_movimientos'), q('finanzas_folios'), q('finanzas_responsabilidad'),
        q('finanzas_descuadres'), q('finanzas_proveedores'), q('finanzas_cancuntrips'),
        q('finanzas_ventas_agente'),
      ]);
      if (cancel) return;
      const fail = [];
      const set = (r, setter, name) => { if (r.error) { fail.push(name); setter([]); } else { setter(r.data || []); } };
      set(r1, setMov, 'movimientos'); set(r2, setFolios, 'folios'); set(r3, setResp, 'responsabilidad');
      set(r4, setDesc, 'descuadres'); set(r5, setProv, 'proveedores'); set(r6, setCtrips, 'cancuntrips');
      set(r7, setVagente, 'ventas_agente');
      setErrs(fail);
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  // ── Rango de periodo (mismo criterio que Fase 7: trimestres calendario) ──
  const hoy = new Date();
  let desde, hasta;
  if (periodo === 'mes') { desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1); hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1); }
  else if (periodo === 'mes-pasado') { desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1); hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 1); }
  else if (periodo === 'q1') { desde = new Date(hoy.getFullYear(), 0, 1); hasta = new Date(hoy.getFullYear(), 3, 1); }
  else if (periodo === 'q2') { desde = new Date(hoy.getFullYear(), 3, 1); hasta = new Date(hoy.getFullYear(), 6, 1); }
  else if (periodo === 'q3') { desde = new Date(hoy.getFullYear(), 6, 1); hasta = new Date(hoy.getFullYear(), 9, 1); }
  else if (periodo === 'q4') { desde = new Date(hoy.getFullYear(), 9, 1); hasta = new Date(hoy.getFullYear() + 1, 0, 1); }
  else if (periodo === 'anio') { desde = new Date(hoy.getFullYear(), 0, 1); hasta = new Date(hoy.getFullYear() + 1, 0, 1); }
  else { desde = new Date(2020, 0, 1); hasta = new Date(2099, 0, 1); }
  const enRango = (f) => { if (!f) return false; const d = new Date(f); return d >= desde && d < hasta; };
  const periodoLabel = (() => {
    if (periodo === 'mes') return `${MESES[hoy.getMonth()]} ${hoy.getFullYear()}`;
    if (periodo === 'mes-pasado') { const m = (hoy.getMonth() - 1 + 12) % 12; const y = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear(); return `${MESES[m]} ${y}`; }
    if (periodo === 'q1') return `Q1 ${hoy.getFullYear()} (ene-mar)`;
    if (periodo === 'q2') return `Q2 ${hoy.getFullYear()} (abr-jun)`;
    if (periodo === 'q3') return `Q3 ${hoy.getFullYear()} (jul-sep)`;
    if (periodo === 'q4') return `Q4 ${hoy.getFullYear()} (oct-dic)`;
    if (periodo === 'anio') return `Año ${hoy.getFullYear()}`;
    return 'Todo el histórico';
  })();

  const agName = (id) => { const a = agentes.find((x) => String(x.id) === String(id)); return a ? (a.nombre || a.id) : (id || '—'); };

  // ════════════════════════════════════════════════════════════════════
  // Estilos base (self-contained, usan C activo)
  // ════════════════════════════════════════════════════════════════════
  // Oleada 2 — sistema Aurelia por tokens (tarjeta glass, tabla, chips).
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 20, marginBottom: 16 };
  const th = { textAlign: 'left', fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 500, padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, color: 'var(--au-text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)' };
  // chip semántico (usa la paleta del sistema; C mirror de los tokens y hace theme-switch).
  const chip = (bg, col) => ({ background: `${bg}20`, color: col, border: `1px solid ${bg}45`, padding: '3px 8px', borderRadius: RADIO_INSIGNIA, fontSize: 12, fontWeight: 600, letterSpacing: '.04em', whiteSpace: 'nowrap' });
  const SecTitle = ({ n, title, corte }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '26px 0 12px', flexWrap: 'wrap' }}>
      <span style={{ width: 3, height: 18, background: 'var(--au-raya)', borderRadius: 2 }} />
      <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', margin: 0, fontFamily: 'var(--au-font-text)' }}>{n}. {title}</h2>
      {corte && <span className="au-badge au-badge--neutral">corte a hoy</span>}
    </div>
  );

  // Switcher de subpestañas (Resumen | Gastos), patrón de los subtabs admin.
  const Subtabs = () => (
    <div className="au-toggle" style={{ marginBottom: 10 }}>
      {[['resumen', 'Panorama'], ['gastos', 'Gastos']].map(([v, l]) => (
        <button key={v} aria-pressed={subtab === v} onClick={() => setSubtab(v)}>{l}</button>
      ))}
    </div>
  );

  // Subpestaña GASTOS: no depende de la carga de las vistas del Resumen.
  if (subtab === 'gastos') {
    return (<div><Subtabs /><GastosTab C={C} adminName={adminName} /></div>);
  }

  if (loading) {
    return (
      <div>
        <Subtabs />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 80 }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--au-glass-border)', borderTop: '3px solid var(--au-gold-solid)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ marginLeft: 14, color: 'var(--au-text-faint)', fontSize: 14 }}>Cargando finanzas…</span>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 1 — Flujo de caja (movimientos es_cash=true en el periodo)
  // ════════════════════════════════════════════════════════════════════
  const movCash = mov.filter((m) => m.es_cash === true && enRango(dcol(m, FECHA_KEYS)));
  const s1_entradas = movCash.filter((m) => str(m, ['tipo']) === 'entrada_cliente').reduce((s, m) => s + num(m, ['monto']), 0);
  const s1_comision = Math.abs(movCash.filter((m) => str(m, ['tipo']) === 'pago_comision').reduce((s, m) => s + num(m, ['monto']), 0));
  const s1_ajusteNeg = Math.abs(movCash.filter((m) => str(m, ['tipo']) === 'ajuste' && num(m, ['monto']) < 0).reduce((s, m) => s + num(m, ['monto']), 0));
  const s1_salidas = s1_comision + s1_ajusteNeg;
  const s1_neto = movCash.reduce((s, m) => s + num(m, ['monto']), 0);
  const s1_depIni = movCash.filter((m) => str(m, ['tipo']) === 'entrada_cliente' && m.es_deposito_inicial === true).reduce((s, m) => s + num(m, ['monto']), 0);
  const s1_abonos = s1_entradas - s1_depIni;

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 2 — Estado de resultados (folios no cancelados en el periodo)
  // ════════════════════════════════════════════════════════════════════
  // Bloque 24: canal de cada folio. Admin = reservas.canal='admin' (o vendido_por_admin_id / sin agente); sin canal → por el agente.
  const isTTf = (f) => f.es_top_tours === true;
  const idFolio = (f) => str(f, ['reserva_id', 'id']);
  const esAdm = (f) => str(f, ['canal']) === 'admin' || foliosAdmin[idFolio(f)] !== undefined || !!str(f, ['vendido_por_admin_id']) || (!str(f, ['agente_id', 'agente']) && !isTTf(f));
  const canalDe = (f) => (esAdm(f) ? 'admin' : isTTf(f) ? 'tt' : 'sala');
  const nombreAdmin = (f) => adminsNombres[foliosAdmin[idFolio(f)] ?? str(f, ['vendido_por_admin_id'])] || 'Admin';
  // Bloque 26: regla del folio admin ("Casa 20 % / Vendedor 80 %" para admin_libre; las viejas, su etiqueta).
  const reglaFolio = (f) => { const r = reglasAdminFolio[idFolio(f)] || { regla: str(f, ['regla_comision']), pct: str(f, ['pct_agente_tt']) }; return r.regla ? etiquetaReglaAdmin(r.regla, r.pct) : ''; };
  const vendedorCelda = (f) => (esAdm(f)
    ? <>{nombreAdmin(f)} <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 800, letterSpacing: '.06em', color: C.naranja, background: `${C.naranja}1f`, border: `1px solid ${C.naranja}55` }}>ADMIN</span>{reglaFolio(f) && <span style={{ marginLeft: 6, fontSize: 12, color: C.muted }}>{reglaFolio(f)}</span>}</>
    : agName(str(f, ['agente_id'])));
  const foliosPerTodos = folios.filter((f) => f.cancelado !== true && enRango(dcol(f, FECHA_KEYS)));
  const foliosPer = canal === 'todos' ? foliosPerTodos : foliosPerTodos.filter((f) => canalDe(f) === canal);
  const porCanal = ['sala', 'tt', 'admin'].map((k) => {
    const fs = foliosPerTodos.filter((f) => canalDe(f) === k);
    return { k, folios: fs.length, venta: fs.reduce((s, f) => s + num(f, ['total']), 0), margen: fs.reduce((s, f) => s + num(f, ['margen']), 0), comision: fs.reduce((s, f) => s + num(f, ['comision_agente']), 0), pendiente: fs.reduce((s, f) => s + num(f, ['saldo_cliente', 'pendiente', 'balance']), 0) };
  });
  const margenAdm = foliosPer.filter(esAdm).reduce((s, f) => s + num(f, ['margen']), 0);
  const comisionAdm = foliosPer.filter(esAdm).reduce((s, f) => s + num(f, ['comision_agente']), 0);
  const s2_venta = foliosPer.reduce((s, f) => s + num(f, ['total']), 0);
  const s2_cargo = foliosPer.reduce((s, f) => s + num(f, ['cargo_pasarela_teorico']), 0);
  const s2_neto = foliosPer.reduce((s, f) => s + num(f, ['precio_neto']), 0);
  const s2_margen = foliosPer.reduce((s, f) => s + num(f, ['margen']), 0);
  const s2_comision = foliosPer.reduce((s, f) => s + num(f, ['comision_agente']), 0);
  const s2_base = s2_venta - s2_cargo;
  const s2_resto = s2_margen - s2_comision;
  const isCT = (f) => f.es_cancuntrips === true;
  const isRP = (f) => f.es_rp_por_fuera === true;
  const isTT = (f) => f.es_top_tours === true;
  // Estándar y CancunTrips excluyen RP y Top Tours; RP va por fuera.
  const margenStd = foliosPer.filter((f) => !isRP(f) && !isCT(f) && !isTT(f) && !esAdm(f)).reduce((s, f) => s + num(f, ['margen']), 0);
  const margenCT = foliosPer.filter((f) => !isRP(f) && isCT(f) && !isTT(f) && !esAdm(f)).reduce((s, f) => s + num(f, ['margen']), 0);
  const margenRP = foliosPer.filter((f) => isRP(f)).reduce((s, f) => s + num(f, ['margen']), 0);
  // Supervisor global del periodo = 5% del margen estándar + CancunTrips (sin RP ni Top Tours).
  const supervisorGlobal = (margenStd + margenCT) * 0.05;
  // Segmentos de la cascada (venta = cargo + neto + comisión + resto casa)
  const cascada = [
    { lbl: 'Cargo pasarela', val: s2_cargo, col: C.muted },
    { lbl: 'Costo neto', val: s2_neto, col: C.blue },
    { lbl: 'Comisión agente', val: s2_comision, col: C.naranja },
    { lbl: 'Resto casa', val: s2_resto, col: C.green },
  ];
  const pct = (v) => (s2_venta > 0 ? Math.max(0, (v / s2_venta) * 100) : 0);

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 3 — Responsabilidad de cobro (estado actual)
  // ════════════════════════════════════════════════════════════════════
  const BUCKETS = [
    { key: 'NUESTRO', color: C.rosa, code: 'BR', desc: 'Representante — lo cobra Cancun100 (nuestra responsabilidad)' },
    { key: 'INTERMEDIO', color: C.yellow, code: 'BT', desc: 'Transporte de llegada' },
    { key: 'PROVEEDOR', color: C.blue, code: 'B1', desc: 'Primer tour — lo cobra el proveedor al abordar' },
    { key: 'PAGADO', color: C.green, code: 'BP', desc: 'Balance pagado' },
    { key: 'SIN CLASIFICAR', color: C.muted, code: '—', desc: 'sin clasificar' },
  ];
  const respLabel = (r) => str(r, ['responsabilidad']).toUpperCase();
  const bucketOf = (label) => {
    const u = (label || '').toUpperCase();
    if (u.includes('NUESTRO')) return 'NUESTRO';
    if (u.includes('INTERMEDIO')) return 'INTERMEDIO';
    if (u.includes('PROVEEDOR')) return 'PROVEEDOR';
    if (u.includes('PAGADO')) return 'PAGADO';
    return 'SIN CLASIFICAR';
  };
  const respByKey = {};
  resp.forEach((r) => { respByKey[bucketOf(respLabel(r))] = r; });
  const foliosDeBucket = (key) => folios
    .filter((f) => bucketOf(str(f, ['responsabilidad'])) === key)
    .sort((a, b) => num(b, ['saldo_cliente']) - num(a, ['saldo_cliente']));

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 4 — Por pagar a proveedores (estado actual)
  // La vista es una fila por folio → se agrega por proveedor aquí.
  // ════════════════════════════════════════════════════════════════════
  const provTotal = prov.reduce((s, p) => s + num(p, ['neto']), 0);
  const provAggMap = {};
  prov.forEach((p) => {
    const codigo = str(p, ['proveedor']);
    const nombre = str(p, ['proveedor_nombre']) || codigo || '—';
    const k = codigo || nombre;
    if (!provAggMap[k]) provAggMap[k] = { codigo, nombre, neto: 0, items: 0, estimado: false };
    const g = provAggMap[k];
    g.neto += num(p, ['neto']);
    g.items += 1;
    if (/estimado/i.test(str(p, ['fuente']))) g.estimado = true;
  });
  const esEspecial = (codigo) => /^(MULTI|SIN_?CLASIF)/i.test(codigo || '');
  const provOrden = Object.values(provAggMap).sort((a, b) => {
    const aEsp = esEspecial(a.codigo), bEsp = esEspecial(b.codigo);
    if (aEsp !== bEsp) return aEsp ? 1 : -1;
    return b.neto - a.neto;
  });

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 5 — Saldos por agente (estado actual)
  // ════════════════════════════════════════════════════════════════════
  const saldoAgMap = {};
  const ensureAg = (id) => { if (!saldoAgMap[id]) saldoAgMap[id] = { id, comision: 0, ajuste: 0 }; return saldoAgMap[id]; };
  folios.filter((f) => f.cancelado !== true).forEach((f) => {
    const id = str(f, ['agente_id', 'agente']);
    if (!id) return;
    ensureAg(id).comision += num(f, ['comision_liberada_no_pagada', 'comision_por_pagar', 'comision_pendiente', 'comision_liberada']);
  });
  mov.filter((m) => str(m, ['tipo']) === 'ajuste' && m.es_cash === false).forEach((m) => {
    const id = str(m, ['agente_id', 'agente']);
    if (!id) return;
    ensureAg(id).ajuste += num(m, ['monto']);
  });
  const saldosAg = Object.values(saldoAgMap)
    .map((r) => ({ ...r, neto: r.comision + r.ajuste }))
    .filter((r) => Math.abs(r.comision) > 0.005 || Math.abs(r.ajuste) > 0.005)
    .sort((a, b) => b.neto - a.neto);

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 6 — Descuadres (filtrable por periodo)
  // ════════════════════════════════════════════════════════════════════
  const descPer = desc.filter((d) => enRango(dcol(d, FECHA_KEYS)));
  const alertasSet = new Set();
  descPer.forEach((d) => { const a = d.alertas; if (Array.isArray(a)) a.forEach((x) => alertasSet.add(String(x))); });
  const descFiltrado = alertaFiltro === 'todas'
    ? descPer
    : descPer.filter((d) => Array.isArray(d.alertas) && d.alertas.map(String).includes(alertaFiltro));

  // ════════════════════════════════════════════════════════════════════
  // SECCIÓN 7 — Reportes CSV (semana lunes→domingo)
  // ════════════════════════════════════════════════════════════════════
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 7);
  const weekLbl = `${ymd(weekStart)} → ${ymd(new Date(weekEnd.getTime() - 86400000))}`;
  // Ambas vistas traen semana_inicio (date, lunes) → se filtra por ese lunes.
  const semLunes = ymd(weekStart);
  const enSemanaInicio = (r) => String(r.semana_inicio || '').slice(0, 10) === semLunes;

  const exportCancuntrips = () => {
    const rows = ctrips.filter(enSemanaInicio);
    const headers = ['folio', 'fecha', 'venta_total', 'descuento_5', 'neto_proveedor', 'margen', 'cancuntrips_20_total', 'cancuntrips_20_devengado', 'responsabilidad'];
    const body = rows.map((r) => [
      str(r, ['folio']), str(r, ['fecha_reserva', 'created_at']),
      num(r, ['venta_total']).toFixed(2), num(r, ['descuento_5']).toFixed(2),
      num(r, ['neto_proveedor']).toFixed(2), num(r, ['margen']).toFixed(2),
      num(r, ['cancuntrips_20_total']).toFixed(2), num(r, ['cancuntrips_20_devengado']).toFixed(2),
      str(r, ['responsabilidad']),
    ]);
    const sumC = (k) => rows.reduce((s, r) => s + num(r, [k]), 0).toFixed(2);
    body.push(['TOTAL', '', sumC('venta_total'), sumC('descuento_5'), sumC('neto_proveedor'), sumC('margen'), sumC('cancuntrips_20_total'), sumC('cancuntrips_20_devengado'), '']);
    downloadCSV(`cancuntrips_${semLunes}.csv`, buildCSV(headers, body));
  };
  const exportVentasAgente = () => {
    // La vista ya viene agregada por agente y por semana → filtrar por semana_inicio.
    const rows = vagente.filter(enSemanaInicio);
    const headers = ['agente', 'folios', 'venta_total', 'margen_total', 'comision_agentes', 'cobrado', 'saldo_pendiente', 'con_comprobante', 'sin_comprobante'];
    const body = rows.map((r) => [
      agName(str(r, ['agente_id'])), String(num(r, ['folios'])),
      num(r, ['venta_total']).toFixed(2), num(r, ['margen_total']).toFixed(2), num(r, ['comision_agentes']).toFixed(2),
      num(r, ['cobrado']).toFixed(2), num(r, ['saldo_pendiente']).toFixed(2),
      String(num(r, ['con_comprobante'])), String(num(r, ['sin_comprobante'])),
    ]);
    const sumV = (k) => rows.reduce((s, r) => s + num(r, [k]), 0);
    body.push(['TOTAL', String(sumV('folios')), sumV('venta_total').toFixed(2), sumV('margen_total').toFixed(2), sumV('comision_agentes').toFixed(2), sumV('cobrado').toFixed(2), sumV('saldo_pendiente').toFixed(2), String(sumV('con_comprobante')), String(sumV('sin_comprobante'))]);
    downloadCSV(`ventas_semana_${semLunes}.csv`, buildCSV(headers, body));
  };
  const moveWeek = (deltaDays) => { const n = new Date(weekStart); n.setDate(n.getDate() + deltaDays); setWeekStart(mondayOf(n)); };

  // KPI grande reutilizable
  const KPIBig = ({ label, value, sub, color, minWidth = 220 }) => (
    <div className="au-glass" style={{ flex: `1 0 ${minWidth}px`, minWidth, borderLeft: `4px solid ${color}`, padding: '16px 18px' }}>
      <div style={{ fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 500 }}>{label}</div>
      <div className="au-num" style={{ fontSize: 30, fontWeight: 600, color, fontFamily: 'var(--au-font-text)', lineHeight: 1.1, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--au-text-faint)', marginTop: 4 }}>{sub}</div>}
    </div>
  );

  // Filtro de periodo: pill del sistema (au-toggle). Devuelve un <button aria-pressed>.
  const pillBtn = (active, onClick, label, key) => (
    <button key={key} aria-pressed={active} onClick={onClick}>{label}</button>
  );

  return (
    <div>
      <Subtabs />
      {errs.length > 0 && (
        <div className="au-banner au-banner--error" style={{ marginBottom: 14, fontSize: 12 }}>
          {<Ic n="AlertTriangle" chico />} No se pudieron leer estas vistas: {errs.join(', ')}. Revisa permisos SELECT / nombre de la vista.
        </div>
      )}

      {/* ── Filtro de periodo ── */}
      <div style={{ padding: '10px 0', borderBottom: '1px solid var(--au-glass-border)', marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="au-toggle" style={{ flexWrap: 'wrap' }}>
            {[['mes', 'Este mes'], ['mes-pasado', 'Mes anterior'], ['q1', 'Q1'], ['q2', 'Q2'], ['q3', 'Q3'], ['q4', 'Q4'], ['anio', 'Año'], ['todos', 'Todo']].map(([v, l]) => pillBtn(periodo === v, () => setPeriodo(v), l, v))}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--au-text-faint)', fontStyle: 'italic' }}>
            Mostrando: <strong style={{ color: 'var(--au-gold-solid)' }}>{periodoLabel}</strong>
          </div>
        </div>
      </div>

      {/* ════ SECCIÓN 1 — FLUJO DE CAJA ════ */}
      <SecTitle n={1} title="Flujo de Caja" />
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
        <KPIBig label="Entradas" value={money(s1_entradas)} sub={`Dep. iniciales ${money(s1_depIni)} · Abonos ${money(s1_abonos)}`} color={C.green} />
        <KPIBig label="Salidas" value={money(s1_salidas)} sub={`Comisiones ${money(s1_comision)} · Ajustes ${money(s1_ajusteNeg)}`} color={C.rosa} />
        <KPIBig label="Flujo neto" value={money(s1_neto)} sub={s1_neto >= 0 ? 'Positivo' : 'Negativo'} color={s1_neto >= 0 ? C.green : C.rosa} />
      </div>

      {/* ════ SECCIÓN 2 — ESTADO DE RESULTADOS ════ */}
      <SecTitle n={2} title="Estado de Resultados" />
      <div style={card}>
        {/* Bloque 24: filtro por canal y totales por canal (Sala · Top Tours · Admin) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div className="au-toggle">
            {[['todos', 'Todos'], ['sala', 'Sala'], ['tt', 'Top Tours'], ['admin', 'Admin']].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={canal === k} onClick={() => setCanal(k)}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>Canal Admin = ventas de administración (Tere, Alan, Marco): su comisión sale de la regla contable del folio; no entran al 5 % de supervisión ni a los $40 de Top Tours.</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 8, marginBottom: 14 }}>
          {porCanal.map((c) => {
            const col = c.k === 'tt' ? COLOR_EQUIPO.tt : c.k === 'admin' ? C.naranja : C.rosa;
            const lbl = c.k === 'tt' ? 'Top Tours' : c.k === 'admin' ? 'Admin' : 'Sala de Ventas';
            return (
              <div key={c.k} onClick={() => setCanal(canal === c.k ? 'todos' : c.k)} style={{ background: `${col}10`, border: `1px solid ${canal === c.k ? col : col + '40'}`, borderRadius: RADIO, padding: '10px 12px', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: col, marginBottom: 6 }}>{lbl} · {c.folios} folio{c.folios !== 1 ? 's' : ''}</div>
                {[['Ventas', c.venta, C.text], ['Margen', c.margen, C.text], ['Comisiones', c.comision, C.naranja], ['Pendiente cliente', c.pendiente, C.rosa]].map(([l, v, cc]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginTop: 2 }}><span>{l}</span><strong style={{ color: cc, fontFamily: 'var(--au-font-text)' }}>{money(v)}</strong></div>
                ))}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: C.muted }}>Cascada del periodo · {foliosPer.length} folio{foliosPer.length !== 1 ? 's' : ''}{canal !== 'todos' && <> · canal {canal === 'tt' ? 'Top Tours' : canal === 'admin' ? 'Admin' : 'Sala'}</>}</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.text, fontFamily: 'var(--au-font-text)' }}>Venta {money(s2_venta)}</div>
        </div>
        {/* Barra cascada proporcional */}
        <div style={{ display: 'flex', height: 34, borderRadius: RADIO_TARJETA, overflow: 'hidden', border: `1px solid ${C.border}` }}>
          {cascada.map((s, i) => (
            <div key={i} title={`${s.lbl}: ${money(s.val)}`} style={{ width: `${pct(s.val)}%`, background: s.col, minWidth: s.val > 0 ? 2 : 0 }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8, marginTop: 12 }}>
          {[['Venta total', s2_venta, C.text], ['− Cargo pasarela', s2_cargo, C.muted], ['= Base', s2_base, C.text], ['− Costo neto', s2_neto, C.blue], ['= Margen', s2_margen, C.text], ['− Comisión agente', s2_comision, C.naranja], ['= Resto casa', s2_resto, C.green]].map(([l, v, col], i) => (
            <div key={i} style={{ background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, padding: '8px 10px' }}>
              <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase' }}>{l}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: col, fontFamily: 'var(--au-font-text)' }}>{money(v)}</div>
            </div>
          ))}
        </div>
        {/* Reparto del margen (informativo, fórmulas Fase 7) */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px dashed ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Reparto del margen (informativo)</div>
            <div style={{ fontSize: 12, color: C.muted }}>Supervisor del periodo (5%): <strong style={{ color: C.blue }}>{money(supervisorGlobal)}</strong> <span style={{ fontSize: 12 }}>(estándar + CancunTrips, sin RP, Top Tours ni Admin)</span></div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 0 200px', background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Estándar (margen {money(margenStd)})</div>
              <div style={{ fontSize: 12, color: C.muted }}>Supervisor 5%: <strong style={{ color: C.text }}>{money(margenStd * 0.05)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted }}>Casa 55%: <strong style={{ color: C.green }}>{money(margenStd * 0.55)}</strong></div>
            </div>
            <div style={{ flex: '1 0 200px', background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>CancunTrips (margen {money(margenCT)})</div>
              <div style={{ fontSize: 12, color: C.muted }}>Supervisor 5%: <strong style={{ color: C.text }}>{money(margenCT * 0.05)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted }}>Casa 35%: <strong style={{ color: C.green }}>{money(margenCT * 0.35)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted }}>CancunTrips 20%: <strong style={{ color: C.text }}>{money(margenCT * 0.20)}</strong></div>
            </div>
            <div style={{ flex: '1 0 200px', background: `${C.purple}12`, border: `1px solid ${C.purple}40`, borderRadius: RADIO_TARJETA, padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.purple, marginBottom: 6 }}>RP por fuera (margen {money(margenRP)})</div>
              <div style={{ fontSize: 12, color: C.muted }}>Agente 80%: <strong style={{ color: C.text }}>{money(margenRP * 0.80)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted }}>Casa 20%: <strong style={{ color: C.green }}>{money(margenRP * 0.20)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>Sin supervisor</div>
            </div>
            {/* Bloque 24: canal Admin, comisión según la regla contable de cada folio */}
            <div style={{ flex: '1 0 200px', background: `${C.naranja}12`, border: `1px solid ${C.naranja}40`, borderRadius: RADIO_TARJETA, padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.naranja, marginBottom: 6 }}>Admin (margen {money(margenAdm)})</div>
              <div style={{ fontSize: 12, color: C.muted }}>Comisión según regla: <strong style={{ color: C.text }}>{money(comisionAdm)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted }}>Casa: <strong style={{ color: C.green }}>{money(margenAdm - comisionAdm)}</strong></div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontStyle: 'italic' }}>Sin supervisor ni $40</div>
            </div>
          </div>
        </div>
      </div>

      {/* ════ SECCIÓN 3 — RESPONSABILIDAD DE COBRO ════ */}
      <SecTitle n={3} title="Responsabilidad de Cobro" corte />
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
            <thead><tr>
              <th style={th}>Bucket</th><th style={{ ...th, textAlign: 'right' }}>Folios</th><th style={{ ...th, textAlign: 'right' }}>Venta</th>
              <th style={{ ...th, textAlign: 'right' }}>Cobrado</th><th style={{ ...th, textAlign: 'right' }}>Saldo</th><th style={{ ...th, textAlign: 'right' }}>Saldo +30d</th>
            </tr></thead>
            <tbody>
              {BUCKETS.map((b) => {
                const r = respByKey[b.key] || {};
                return (
                  <tr key={b.key} onClick={() => setModal({ label: b.key, color: b.color, rows: foliosDeBucket(b.key) })}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = `${b.color}10`)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                    <td style={td}><span style={chip(b.color, b.color)}>{b.key}</span></td>
                    <td style={{ ...td, textAlign: 'right' }}>{num(r, ['folios'])}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(num(r, ['venta']))}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(num(r, ['cobrado']))}</td>
                    <td style={{ ...td, textAlign: 'right', color: b.color, fontWeight: 700 }}>{money(num(r, ['saldo']))}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(num(r, ['saldo_mas_30d']))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {BUCKETS.filter((b) => b.code !== '—').map((b) => (
            <span key={b.code}><strong style={{ color: b.color }}>{b.code}</strong> = {b.desc}</span>
          ))}
          <span>· Clic en una fila para ver sus folios.</span>
        </div>
      </div>

      {/* ════ SECCIÓN 4 — POR PAGAR A PROVEEDORES ════ */}
      <SecTitle n={4} title="Por Pagar a Proveedores" corte />
      <div style={{ ...card, background: `linear-gradient(135deg,${C.blue}12,${C.purple}12)`, border: `1px solid ${C.blue}40` }}>
        <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Xperiencias Caribe (proveedor absoluto)</div>
        <div style={{ fontSize: 30, fontWeight: 800, color: C.blue, fontFamily: 'var(--au-font-text)', marginTop: 4 }}>{money(provTotal)}</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Suma total de netos por pagar.</div>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
            <thead><tr>
              <th style={th}>Proveedor</th><th style={{ ...th, textAlign: 'right' }}>Neto total</th><th style={{ ...th, textAlign: 'right' }}>Items</th><th style={{ ...th, textAlign: 'center' }}>Fuente</th>
            </tr></thead>
            <tbody>
              {provOrden.map((p, i) => {
                const especial = esEspecial(p.codigo);
                const exacto = !p.estimado;
                return (
                  <tr key={i}>
                    <td style={td} title={especial ? 'Histórico sin desglose por proveedor' : undefined}>
                      {p.nombre}{especial && <span style={{ ...chip(C.muted, C.muted), marginLeft: 6 }}>histórico</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(p.neto)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{p.items}</td>
                    <td style={{ ...td, textAlign: 'center' }}><span style={chip(exacto ? C.green : C.yellow, exacto ? C.green : C.yellow)}>{exacto ? 'exacto' : 'estimado'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.yellow }}>
          Estimado. Registro de pagos reales a proveedores llega en v2.
        </div>
      </div>

      {/* ════ SECCIÓN 5 — SALDOS POR AGENTE ════ */}
      <SecTitle n={5} title="Saldos por Agente" corte />
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr>
              <th style={th}>Agente</th>
              <th style={{ ...th, textAlign: 'right' }}>Comisión por pagar</th>
              <th style={{ ...th, textAlign: 'right' }}>Ajustes no saldados</th>
              <th style={{ ...th, textAlign: 'right' }}>Neto</th>
            </tr></thead>
            <tbody>
              {saldosAg.length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={4}>Sin saldos abiertos.</td></tr>}
              {saldosAg.map((r) => {
                const netCol = r.neto > 0.005 ? C.green : r.neto < -0.005 ? C.rosa : C.muted;
                return (
                  <tr key={r.id}>
                    <td style={td}>{agName(r.id)}</td>
                    <td style={{ ...td, textAlign: 'right', color: C.green }}>{money2(r.comision)}</td>
                    <td style={{ ...td, textAlign: 'right', color: r.ajuste < 0 ? C.rosa : C.text }}>{money2(r.ajuste)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: netCol }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: netCol }} />{money2(r.neto)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ════ SECCIÓN 6 — DESCUADRES ════ */}
      <SecTitle n={6} title="Descuadres" />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ background: 'var(--au-error-bg)', border: '1px solid var(--au-error-bd)', borderRadius: RADIO_TARJETA, padding: '10px 18px' }}>
          <div style={{ fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Descuadres en {periodoLabel}</div>
          <div className="au-num" style={{ fontSize: 30, fontWeight: 600, color: 'var(--au-error-fg)', fontFamily: 'var(--au-font-text)' }}>{descFiltrado.length}</div>
        </div>
        <select className="au-input" value={alertaFiltro} onChange={(e) => setAlertaFiltro(e.target.value)}
          style={{ width: 'auto', cursor: 'pointer', colorScheme: 'dark' }}>
          <option value="todas">Todas las alertas</option>
          {[...alertasSet].sort().map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead><tr>
              <th style={th}>Folio</th><th style={th}>Agente</th><th style={{ ...th, textAlign: 'right' }}>Total</th>
              <th style={{ ...th, textAlign: 'right' }}>Pagado</th><th style={{ ...th, textAlign: 'right' }}>Saldo</th>
              <th style={{ ...th, textAlign: 'right' }}>Días</th><th style={th}>Alertas</th>
            </tr></thead>
            <tbody>
              {descFiltrado.length === 0 && <tr><td style={{ ...td, color: C.muted }} colSpan={7}>Sin descuadres.</td></tr>}
              {descFiltrado.slice(0, 400).map((d, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontFamily: 'var(--au-font-text)', fontWeight: 700 }}>{str(d, ['folio_personalizado', 'folio'])}</td>
                  <td style={td}>{vendedorCelda(d)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{money2(num(d, ['total']))}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{money2(num(d, ['pagado_cliente']))}</td>
                  <td style={{ ...td, textAlign: 'right', color: C.rosa, fontWeight: 700 }}>{money2(num(d, ['saldo_cliente']))}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{num(d, ['dias_antiguedad'])}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {Array.isArray(d.alertas) && d.alertas.map((a, j) => <span key={j} style={chip(C.rosa, C.rosa)}>{String(a)}</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {descFiltrado.length > 400 && <div style={{ padding: '8px 14px', fontSize: 12, color: C.muted }}>Mostrando 400 de {descFiltrado.length}.</div>}
      </div>

      {/* ════ SECCIÓN 7 — REPORTES DESCARGABLES ════ */}
      <SecTitle n={7} title="Reportes Descargables (CSV)" corte />
      <div style={card}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={() => moveWeek(-7)} className="au-btn au-btn--secondary" style={{ padding: '6px 12px' }}>{<Ic n="ChevronLeft" chico />}</button>
          <div className="au-num" style={{ fontSize: 12, color: 'var(--au-text-primary)', fontWeight: 500 }}>Semana: <span style={{ color: 'var(--au-gold-solid)' }}>{weekLbl}</span></div>
          <button onClick={() => moveWeek(7)} className="au-btn au-btn--secondary" style={{ padding: '6px 12px' }}>{<Ic n="ChevronRight" chico />}</button>
          <button onClick={() => setWeekStart(() => { const m = mondayOf(new Date()); m.setDate(m.getDate() - 7); return m; })}
            className="au-btn au-btn--ghost" style={{ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>Semana pasada</button>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={exportCancuntrips} className="au-btn au-btn--secondary">⬇ Reporte CancunTrips</button>
          <button onClick={exportVentasAgente} className="au-btn au-btn--secondary">⬇ Reporte Ventas por Vendedor</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--au-text-faint)', marginTop: 10 }}>UTF-8 con BOM · separador coma · montos con 2 decimales sin símbolo. CancunTrips es confidencial (convenio).</div>
      </div>

      {/* ── Modal folios por bucket (sección 3) ── */}
      {modal && (
        <div onClick={() => setModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20, backdropFilter: 'blur(6px)' }}>
          <div onClick={(e) => e.stopPropagation()} className="au-glass au-glass--strong" style={{ maxWidth: 640, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ position: 'sticky', top: 0, background: 'var(--au-glass-fill-strong)', backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', padding: '16px 18px', borderBottom: '1px solid var(--au-glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={chip(modal.color, modal.color)}>{modal.label}</span>
                <span style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>{modal.rows.length} folio{modal.rows.length !== 1 ? 's' : ''}</span>
              </div>
              <button onClick={() => setModal(null)} className="au-btn au-btn--ghost" style={{ padding: 0, width: 30, height: 30, justifyContent: 'center' }}>{<Ic n="X" chico />}</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead><tr>
                  <th style={th}>Folio</th><th style={th}>Agente</th><th style={{ ...th, textAlign: 'right' }}>Venta</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cobrado</th><th style={{ ...th, textAlign: 'right' }}>Saldo</th><th style={{ ...th, textAlign: 'right' }}>Días</th>
                </tr></thead>
                <tbody>
                  {modal.rows.slice(0, 300).map((f, i) => (
                    <tr key={i}>
                      <td style={{ ...td, fontFamily: 'var(--au-font-text)', fontWeight: 700 }}>{str(f, ['folio_personalizado', 'folio'])}</td>
                      <td style={td}>{vendedorCelda(f)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{money(num(f, ['total']))}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{money(num(f, ['pagado_cliente']))}</td>
                      <td style={{ ...td, textAlign: 'right', color: modal.color, fontWeight: 700 }}>{money(num(f, ['saldo_cliente']))}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{num(f, ['dias_antiguedad'])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
